import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate } from 'react-router';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GetRecurringInvoiceDocument,
  GetRecurringInvoiceQuery,
  GetRecurringInvoiceQueryVariables,
  UpdateRecurringInvoiceDocument,
  UpdateRecurringInvoiceMutation,
  UpdateRecurringInvoiceMutationVariables,
  DeleteRecurringInvoiceDocument,
  DeleteRecurringInvoiceMutation,
  DeleteRecurringInvoiceMutationVariables,
  UndeleteRecurringInvoiceDocument,
  UndeleteRecurringInvoiceMutation,
  UndeleteRecurringInvoiceMutationVariables,
  SuspendRecurringInvoiceDocument,
  SuspendRecurringInvoiceMutation,
  SuspendRecurringInvoiceMutationVariables,
  UnsuspendRecurringInvoiceDocument,
  UnsuspendRecurringInvoiceMutation,
  UnsuspendRecurringInvoiceMutationVariables,
  CopyRecurringInvoiceDocument,
  CopyRecurringInvoiceMutation,
  CopyRecurringInvoiceMutationVariables,
  AddRecurringInvoiceRowDocument,
  AddRecurringInvoiceRowMutation,
  AddRecurringInvoiceRowMutationVariables,
  UpdateRecurringInvoiceRowDocument,
  UpdateRecurringInvoiceRowMutation,
  UpdateRecurringInvoiceRowMutationVariables,
  RemoveRecurringInvoiceRowDocument,
  RemoveRecurringInvoiceRowMutation,
  RemoveRecurringInvoiceRowMutationVariables,
  MoveRecurringInvoiceRowUpDocument,
  MoveRecurringInvoiceRowUpMutation,
  MoveRecurringInvoiceRowUpMutationVariables,
  MoveRecurringInvoiceRowDownDocument,
  MoveRecurringInvoiceRowDownMutation,
  MoveRecurringInvoiceRowDownMutationVariables,
  RecurringInvoiceFrequency,
  SetRecurringInvoiceUnitAssignmentsDocument,
  SetRecurringInvoiceUnitAssignmentsMutation,
  SetRecurringInvoiceUnitAssignmentsMutationVariables,
} from '../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../components/app-context';
import { BasePage } from '../../components/base-page';
import { appPaths } from '../../app-paths';
import { RecurringInvoiceRowEditor } from './components/recurring-invoice-row-editor';
import ClearIcon from '@mui/icons-material/Clear';
import { AddIcon, RemoveItemIcon } from '../../components/icons';
import { InvoiceIncomeAssignmentPanel } from '../invoice-detail/components/invoice-income-assignment';
import { RecurringInvoiceIncomeAssignmentPanel } from './components/income-assignment-panel';

interface FormFields {
  nextDate: string;
  frequency: RecurringInvoiceFrequency;
  stopAfterDate: string;
  dueDays: string;
  description: string;
}

const frequencyOptions: Array<{ value: RecurringInvoiceFrequency; label: string }> = [
  { value: 'W' as RecurringInvoiceFrequency, label: 'týždenne' },
  { value: 'W2' as RecurringInvoiceFrequency, label: 'každé 2 týždne' },
  { value: 'M' as RecurringInvoiceFrequency, label: 'každý mesiac' },
  { value: 'M2' as RecurringInvoiceFrequency, label: 'každé dva mesiace' },
  { value: 'M3' as RecurringInvoiceFrequency, label: 'každé 3 mesiace' },
  { value: 'M6' as RecurringInvoiceFrequency, label: 'každých 6 mesiacov' },
  { value: 'Y' as RecurringInvoiceFrequency, label: 'ročne' },
];

export function RecurringInvoiceDetailPage() {
  const app = useApp();
  const { show } = useAlert();
  const navigate = useNavigate();
  const { recurringInvoiceId } = useParams();

  const [formData, setFormData] = useState<FormFields>({
    nextDate: new Date().toISOString().split('T')[0],
    frequency: 'M' as RecurringInvoiceFrequency,
    stopAfterDate: '',
    dueDays: '14',
    description: '',
  });

  const [fetchRecurringInvoice, { data, loading: initialLoading, error, refetch }] = useLazyQuery<
    GetRecurringInvoiceQuery,
    GetRecurringInvoiceQueryVariables
  >(GetRecurringInvoiceDocument);

  useEffect(() => {
    if (recurringInvoiceId) {
      fetchRecurringInvoice({ variables: { id: recurringInvoiceId } });
    }
  }, [recurringInvoiceId, fetchRecurringInvoice]);

  const recurringInvoice = data?.getRecurringInvoice;

  useEffect(() => {
    if (recurringInvoice) {
      setFormData({
        nextDate: recurringInvoice.nextDate ? new Date(recurringInvoice.nextDate).toISOString().substring(0, 10) : '',
        frequency: (recurringInvoice.frequency as RecurringInvoiceFrequency) || ('M' as RecurringInvoiceFrequency),
        stopAfterDate: recurringInvoice.stopAfterDate
          ? new Date(recurringInvoice.stopAfterDate).toISOString().substring(0, 10)
          : '',
        dueDays: String(recurringInvoice.dueDays || 14),
        description: recurringInvoice.description || '',
      });
    }
  }, [recurringInvoice]);

  const [updateRecurringInvoice, { loading: updateLoading }] = useMutation<
    UpdateRecurringInvoiceMutation,
    UpdateRecurringInvoiceMutationVariables
  >(UpdateRecurringInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa upraviť pravidelnú faktúru.', e.message);
    },
    onCompleted: () => {
      show.info('Pravidelná faktúra bola úspešne upravená.');
      refetch?.();
    },
  });

  const [deleteRecurringInvoice, { loading: deleteLoading }] = useMutation<
    DeleteRecurringInvoiceMutation,
    DeleteRecurringInvoiceMutationVariables
  >(DeleteRecurringInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa zmazať pravidelnú faktúru.', e.message);
    },
    onCompleted: () => {
      show.info('Pravidelná faktúra bola úspešne zmazaná.');
      refetch?.();
    },
  });

  const [undeleteRecurringInvoice, { loading: undeleteLoading }] = useMutation<
    UndeleteRecurringInvoiceMutation,
    UndeleteRecurringInvoiceMutationVariables
  >(UndeleteRecurringInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa obnoviť pravidelnú faktúru.', e.message);
    },
    onCompleted: () => {
      show.info('Pravidelná faktúra bola úspešne obnovená.');
      refetch?.();
    },
  });

  const [suspendRecurringInvoice, { loading: suspendLoading }] = useMutation<
    SuspendRecurringInvoiceMutation,
    SuspendRecurringInvoiceMutationVariables
  >(SuspendRecurringInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa pozastaviť pravidelnú faktúru.', e.message);
    },
    onCompleted: () => {
      show.info('Pravidelná faktúra bola pozastavená.');
      refetch?.();
    },
  });

  const [unsuspendRecurringInvoice, { loading: unsuspendLoading }] = useMutation<
    UnsuspendRecurringInvoiceMutation,
    UnsuspendRecurringInvoiceMutationVariables
  >(UnsuspendRecurringInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa obnoviť pravidelnú faktúru.', e.message);
    },
    onCompleted: () => {
      show.info('Pravidelná faktúra bola obnovená.');
      refetch?.();
    },
  });

  const [copyRecurringInvoice, { loading: copyLoading }] = useMutation<
    CopyRecurringInvoiceMutation,
    CopyRecurringInvoiceMutationVariables
  >(CopyRecurringInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa skopírovať pravidelnú faktúru.', e.message);
    },
    onCompleted: (data) => {
      if (data?.copyRecurringInvoice?.recurringInvoice) {
        show.info('Pravidelná faktúra bola úspešne skopírovaná.');
        navigate(
          appPaths.recurringInvoice.navigate({ recurringInvoiceId: data.copyRecurringInvoice.recurringInvoice.id }),
        );
      }
    },
  });

  const [addRow] = useMutation<AddRecurringInvoiceRowMutation, AddRecurringInvoiceRowMutationVariables>(
    AddRecurringInvoiceRowDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa pridať riadok.', e.message);
      },
    },
  );

  const [updateRow] = useMutation<UpdateRecurringInvoiceRowMutation, UpdateRecurringInvoiceRowMutationVariables>(
    UpdateRecurringInvoiceRowDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa upraviť riadok.', e.message);
      },
    },
  );

  const [removeRow] = useMutation<RemoveRecurringInvoiceRowMutation, RemoveRecurringInvoiceRowMutationVariables>(
    RemoveRecurringInvoiceRowDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa zmazať riadok.', e.message);
      },
      onCompleted: () => {
        show.info('Riadok bol zmazaný.');
        refetch?.();
      },
    },
  );

  const [moveRowUp] = useMutation<MoveRecurringInvoiceRowUpMutation, MoveRecurringInvoiceRowUpMutationVariables>(
    MoveRecurringInvoiceRowUpDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa posunúť riadok.', e.message);
      },
      onCompleted: () => {
        refetch?.();
      },
    },
  );

  const [moveRowDown] = useMutation<MoveRecurringInvoiceRowDownMutation, MoveRecurringInvoiceRowDownMutationVariables>(
    MoveRecurringInvoiceRowDownDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa posunúť riadok.', e.message);
      },
      onCompleted: () => {
        refetch?.();
      },
    },
  );

  const [setUnitAssignments, { loading: assignmentsLoading }] = useMutation<
    SetRecurringInvoiceUnitAssignmentsMutation,
    SetRecurringInvoiceUnitAssignmentsMutationVariables
  >(SetRecurringInvoiceUnitAssignmentsDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa uložiť priradenia príjmov.', e.message),
    onCompleted: () => {
      show.info('Priradenia príjmov boli úspešne uložené.');
      refetch?.();
    },
  });

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!recurringInvoice) return;

    await updateRecurringInvoice({
      variables: {
        input: {
          id: recurringInvoice.id,
          nextDate: formData.nextDate ? new Date(formData.nextDate).toISOString() : undefined,
          frequency: formData.frequency,
          stopAfterDate: formData.stopAfterDate ? new Date(formData.stopAfterDate).toISOString() : undefined,
          dueDays: parseInt(formData.dueDays) || 14,
          description: formData.description,
        },
      },
    });
  };

  const handleDelete = async () => {
    if (!recurringInvoice) return;

    if (recurringInvoice.deletedAt) {
      await undeleteRecurringInvoice({
        variables: {
          recurringInvoiceId: recurringInvoice.id,
        },
      });
    } else {
      await deleteRecurringInvoice({
        variables: {
          recurringInvoiceId: recurringInvoice.id,
        },
      });
    }
  };

  const handleSuspend = async () => {
    if (!recurringInvoice) return;

    if (recurringInvoice.suspended) {
      await unsuspendRecurringInvoice({
        variables: {
          recurringInvoiceId: recurringInvoice.id,
        },
      });
    } else {
      await suspendRecurringInvoice({
        variables: {
          recurringInvoiceId: recurringInvoice.id,
        },
      });
    }
  };

  const handleCopy = async () => {
    if (!recurringInvoice) return;

    await copyRecurringInvoice({
      variables: {
        recurringInvoiceId: recurringInvoice.id,
      },
    });
  };

  const clearStopAfterDate = () => {
    setFormData({ ...formData, stopAfterDate: '' });
  };

  if (!recurringInvoice) {
    return (
      <BasePage
        title="Pravidelná faktúra"
        loading={initialLoading}
        errorMsg={error ? 'Nepodarilo sa načítať detaily pravidelnej faktúry.' : undefined}
      >
        <div />
      </BasePage>
    );
  }

  const isDeleted = !!recurringInvoice.deletedAt;
  const isSuspended = recurringInvoice.suspended;
  const canEdit = !isDeleted;
  const loading =
    updateLoading ||
    deleteLoading ||
    undeleteLoading ||
    suspendLoading ||
    unsuspendLoading ||
    copyLoading ||
    assignmentsLoading;

  const frequencyLabel =
    frequencyOptions.find((opt) => opt.value === recurringInvoice.frequency)?.label || recurringInvoice.frequency;

  return (
    <BasePage title={`Pravidelná faktúra - ${frequencyLabel}`}>
      <Grid container spacing={3}>
        {/* Left side - Recurring invoice details and rows */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={3}>
            {/* Status messages */}
            {isDeleted && (
              <Typography variant="body1" color="error">
                Táto pravidelná faktúra bola zmazaná dňa {app.formatDate(recurringInvoice.deletedAt)}
              </Typography>
            )}
            {isSuspended && !isDeleted && (
              <Typography variant="body1" color="warning.main">
                Táto pravidelná faktúra je pozastavená
              </Typography>
            )}

            {/* Recurring invoice fields */}
            {/* Contract link */}
            {recurringInvoice.contract && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Prenájom:</strong>{' '}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate(appPaths.contract.navigate({ contractId: recurringInvoice.contract!.id }))}
                    sx={{ cursor: 'pointer' }}
                  >
                    {recurringInvoice.contract.title}
                  </Link>
                </Typography>
              </Box>
            )}
            <Box component="form" noValidate>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="nextDate"
                    label="Dátum ďalšej faktúry"
                    type="date"
                    value={formData.nextDate}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="frequency"
                    label="Frekvencia"
                    select
                    value={formData.frequency}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    fullWidth
                    required
                  >
                    {frequencyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="stopAfterDate"
                    label="Zastaviť po (voliteľné)"
                    type="date"
                    value={formData.stopAfterDate}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: formData.stopAfterDate && (
                        <Box
                          component="button"
                          type="button"
                          onClick={clearStopAfterDate}
                          sx={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: 0,
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </Box>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="dueDays"
                    label="Splatnosť (počet dní)"
                    type="number"
                    value={formData.dueDays}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    name="description"
                    label="Popis"
                    value={formData.description}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    fullWidth
                    required
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>

              {canEdit && (
                <Button variant="contained" onClick={handleSave} disabled={loading} sx={{ mt: 2 }}>
                  Uložiť zmeny
                </Button>
              )}
            </Box>

            <Divider />

            {/* Recurring invoice rows */}
            {recurringInvoice && (
              <RecurringInvoiceRowEditor
                recurringInvoice={recurringInvoice}
                disabled={!canEdit}
                onAddRow={(row) => addRow({ variables: { recurringInvoiceId: recurringInvoice.id, input: row } })}
                onUpdateRow={(rowNumber, row) =>
                  updateRow({ variables: { recurringInvoiceId: recurringInvoice.id, rowNumber, input: row } })
                }
                onRemoveRow={(rowNumber) =>
                  removeRow({ variables: { recurringInvoiceId: recurringInvoice.id, rowNumber } })
                }
                onMoveRowUp={(rowNumber) =>
                  moveRowUp({ variables: { recurringInvoiceId: recurringInvoice.id, rowNumber } })
                }
                onMoveRowDown={(rowNumber) =>
                  moveRowDown({ variables: { recurringInvoiceId: recurringInvoice.id, rowNumber } })
                }
              />
            )}

            <Divider />

            {/* Unit assignments */}
            <RecurringInvoiceIncomeAssignmentPanel
              recurringInvoice={recurringInvoice}
              loading={assignmentsLoading}
              onSaveAssignments={async (assignments) => {
                await setUnitAssignments({
                  variables: {
                    recurringInvoiceId: recurringInvoice.id,
                    assignments: assignments.map((r) => ({ unitId: r.unitId, amount: parseFloat(r.amount) })),
                  },
                });
              }}
            />
          </Stack>
        </Grid>

        {/* Right side - Actions */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={2}>
            {/* Action buttons */}
            <Box>
              <Stack spacing={1}>
                <Button onClick={handleCopy} disabled={loading} color="primary" variant="outlined" fullWidth>
                  Kopírovať
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={loading}
                  color={isDeleted ? 'primary' : 'error'}
                  variant="outlined"
                  fullWidth
                >
                  {isDeleted ? 'Obnoviť' : 'Zmazať'}
                </Button>
                {!isDeleted && (
                  <Button
                    onClick={handleSuspend}
                    disabled={loading}
                    color={isSuspended ? 'success' : 'warning'}
                    variant="outlined"
                    fullWidth
                  >
                    {isSuspended ? 'Spustiť' : 'Pozastaviť'}
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </BasePage>
  );
}
